import { Directive, ElementRef, OnDestroy, OnInit, inject, effect, EffectRef, Injector, Input } from '@angular/core';
import { VTranslateService } from '../../services/v-translate.service';

/**
 * Please be considerate when leaving console logs in often used code like this directive. 
 * It will spam the console and make it hard to find relevant information.
 */
const DEBUG = false;

/*
 * Usage:
 * <div [vTranslate]="'hello.world'"></div>
 * <div [vTranslate]="'user.greeting'" [vTranslateParams]="{ name: 'John' }"></div>
 * <div vTranslate="hello.world"></div>
 * <div vTranslate="user.greeting" [vTranslateParams]="{ name: 'John' }"></div>
 * 
 * Example:
 * <p vTranslate="welcome.message"></p>
 * <span [vTranslate]="'notifications.count'" [vTranslateParams]="{ count: 5 }"></span>
 */
@Directive({
    selector:   '[vtranslate]',
    standalone: true
})
export class VTranslateDirective implements OnInit, OnDestroy {
    private translateService = inject(VTranslateService);
    private el = inject(ElementRef);
    private injector = inject(Injector);

    private cache = new Map<string, { signal: ReturnType<VTranslateService['asSignal']>, lastData: unknown }>();
    private currentToken: string = '';
    private currentParams: unknown;
    private effectRef?: EffectRef;

    @Input()
    set vtranslate(token: string) {
        if (DEBUG) console.log('VTranslateDirective: Setting token via input', token);
        this.currentToken = token || '';
        this.updateTranslation();
    }

    @Input()
    set vTranslateParams(params: unknown) {
        if (DEBUG) console.log('VTranslateDirective: Setting params', params);
        this.currentParams = params;
        this.updateTranslation();
    }

    constructor() {
        if (DEBUG) console.log('VTranslateDirective: Constructor called!');
    }

    ngOnInit(): void {
        // If no token was provided via input, get it from the element's text content
        if (!this.currentToken) {
            this.currentToken = this.el.nativeElement?.textContent?.trim() || '';
            if (DEBUG) console.log('VTranslateDirective: Found token in element text:', this.currentToken);
        }
        this.updateTranslation();
    }

    ngOnDestroy(): void {
        if (this.effectRef)
            this.effectRef.destroy();
    }

    private updateTranslation(): void {
        if (DEBUG) console.log('VTranslateDirective: updateTranslation called', { token: this.currentToken, params: this.currentParams });
        if (!this.currentToken) return;

        const cacheKey = `${this.currentToken}:${JSON.stringify(this.currentParams)}`;
        const cached = this.cache.get(cacheKey);

        // Check if we have a cached signal and if the params haven't changed
        if (cached && this.dataEquals(cached.lastData, this.currentParams)) {
            if (DEBUG) console.log('VTranslateDirective: Using cached signal');
            let translation = cached.signal();
            translation = typeof translation === 'string' ? translation : this.currentToken; // Fallback to token if translation is not a string
            this.setElementText(translation);
            return;
        }

        // Create new signal and cache it
        if (DEBUG) console.log('VTranslateDirective: Creating new signal');
        const signal = this.translateService.asSignal(this.currentToken, this.currentParams);
        this.cache.set(cacheKey, { signal, lastData: this.currentParams });

        // Clean up previous effect
        if (this.effectRef)
            this.effectRef.destroy();

        // Create effect to watch signal changes
        this.effectRef = effect(() => {
            let translation = signal();
            translation = typeof translation === 'string' ? translation : this.currentToken; // Fallback to token if translation is not a string

            if (DEBUG) console.log('VTranslateDirective: Effect triggered, translation:', translation);
            this.setElementText(translation);
        }, { injector: this.injector });
    }

    private setElementText(text: string): void {
        if (DEBUG) console.log('VTranslateDirective: Setting element text to:', text);
        if (this.el?.nativeElement)
            this.el.nativeElement.textContent = text;
    }

    private dataEquals(a: unknown, b: unknown): boolean {
        if (a === b) return true;
        if (!a && !b) return true;
        if (!a || !b) return false;

        // Simple deep comparison for objects
        if (typeof a === 'object' && typeof b === 'object') {
            const keysA = Object.keys(a as Record<string, unknown>);
            const keysB = Object.keys(b as Record<string, unknown>);

            if (keysA.length !== keysB.length) return false;

            for (const key of keysA)
                if ((a as Record<string, unknown>)[key] !== (b as Record<string, unknown>)[key]) return false;

            return true;
        }

        return false;
    }
}
